import { useEffect, useState } from 'react';
import { Wand2, FileText } from 'lucide-react';
import { AnimatedIconButton } from '../../../common/AnimatedIconButton';
import { EmptyState } from '../../../common/EmptyState';
import { PostCard } from '../PostCard';
import { SetupWizard } from '../../onboarding/SetupWizard';
import { SetupExpress } from '../../onboarding/SetupExpress';
import { useSetupWizard } from '../../../../hooks/useSetupWizard';
import { useOnboardingPreference } from '../../../../hooks/useOnboardingPreference';
import { Skeleton } from '../../../ui/skeleton';
import type { Post, CommunicationChannel, WatchTopic, Source } from '../../../../types';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { brandConfigService } from '../../../../services/brandConfigService';
import { SIDEBAR_ITEMS } from '../../../../utils/Constants';

interface PostGalleryGridProps {
  isMobile: boolean;
  filteredPosts: Post[];
  numColumns: number;
  cardWidth: number;
  gridGap: number;
  gridContainerRef: React.RefObject<HTMLDivElement>;
  onOpenGenerateModal: () => void;
  setSelectedPost: (post: Post) => void;
  setDeletingPost: (post: Post) => void;
  communicationChannels: CommunicationChannel[];
  onOpenSidebarTab?: (tabId: string) => void;
  systemPrompt?: string;
  isPositioningTemplate?: boolean;
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — topics/sources optionnels si veille off.
  sectorWatchEnabled?: boolean;
  watchTopics?: WatchTopic[];
  sources?: Source[];
  // V4-UX-SETUP-EXPRESS-SILENT-REFRESH — `silent` recharge sans skeleton (voir useRaisoData).
  onRefreshData?: (opts?: { silent?: boolean }) => Promise<void>;
  isDataLoading?: boolean;
  // V4-FRONT-WIZARD-POST-GEN-HIDE-VERIFY 2026-05-29 — raw count avant filtrage
  // status. Évite que le wizard ré-apparaisse quand l'user filtre un status
  // vide (ex : drafts only + filtre "Publié" → filteredPosts=[] mais l'user a
  // déjà ≥1 post quelque part). Si non fourni, fallback à filteredPosts.length
  // (backward-compat).
  hasAnyPost?: boolean;
}

export function PostGalleryGrid({
  isMobile,
  filteredPosts,
  numColumns,
  cardWidth,
  gridGap,
  gridContainerRef,
  onOpenGenerateModal,
  setSelectedPost,
  setDeletingPost,
  communicationChannels,
  onOpenSidebarTab,
  systemPrompt = "",
  isPositioningTemplate = false,
  sectorWatchEnabled,
  watchTopics = [],
  sources = [],
  onRefreshData,
  isDataLoading = false,
  hasAnyPost: hasAnyPostProp
}: PostGalleryGridProps) {
  const { t } = useLanguage();
  const { currentSpace, isReadOnly, isGuest, user } = useAuthContext();
  const { isComplete: isSetupComplete, steps: setupSteps } = useSetupWizard({
    systemPrompt,
    isPositioningTemplate,
    watchTopics,
    sources,
    communicationChannels,
    sectorWatchEnabled,
  });

  // V4-5 — Check si la charte graphique est manquante pour proposer la saisie
  // manuelle à la fin du wizard.
  const [brandConfigMissing, setBrandConfigMissing] = useState(false);
  useEffect(() => {
    if (!currentSpace?.id) return;
    let cancelled = false;
    brandConfigService.get(currentSpace.id)
      .then((cfg) => {
        if (cancelled) return;
        // V4-WIZARD-BRAND-FALSE-POSITIVE 2026-06-18 — Le check ne doit PAS se limiter
        // à colors/fonts/images : une charte renseignée uniquement via la direction
        // artistique + tags (BrandConfigContent mode manuel) était considérée à tort
        // comme "manquante" → alerte affichée en permanence. On considère la charte
        // présente dès QU'UN signal exploitable existe (couleurs, fonts, images,
        // direction artistique OU mots-clés). Non contraignant : on se débrouille
        // avec ce qu'on a au lieu de créer un faux positif bloquant.
        //
        // V4-WIZARD-BRAND-FALSE-POSITIVE-CHECK 2026-07-11 — Cas résiduel non couvert :
        // pendant l'extraction (`extraction_status === 'pending'`, job arq en cours),
        // les listes sont vides PAR CONSTRUCTION (cf. docstring BrandConfig) → le check
        // ci-dessous les voyait "manquantes" et affichait le CTA à tort alors que
        // l'extraction va peupler la charte. On ne flague PAS "manquante" tant que
        // l'extraction est en cours ; `failed`/`succeeded`-mais-vide restent bien
        // signalés (CTA de saisie manuelle légitime).
        const stillExtracting = cfg?.extraction_status === 'pending';
        const isEmpty = !stillExtracting && (!cfg || (
          (cfg.colors?.length ?? 0) === 0
          && (cfg.fonts?.length ?? 0) === 0
          && (cfg.images?.length ?? 0) === 0
          && !(cfg.artistic_direction?.trim())
          && (cfg.direction_keywords?.length ?? 0) === 0
        ));
        setBrandConfigMissing(isEmpty);
      })
      .catch(() => {
        // Non-bloquant : si fetch échoue, on n'affiche pas la CTA (mieux que faux positif)
        if (!cancelled) setBrandConfigMissing(false);
      });
    return () => { cancelled = true; };
    // V4-BRAND-CONFIG-LOAD-RACE 2026-05-29 — retiré isSetupComplete de la deps array.
    // La charte ne dépend pas du setup, cache 5min de brandConfigService suffit.
  }, [currentSpace?.id]);

  // V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29 — Préférence per-space pour
  // afficher ou masquer le wizard. Default true (visible) pour ne pas casser
  // le 1er setup. Toggle dans Settings permet à l'user de réafficher.
  const { visible: onboardingVisible, setVisible: setOnboardingVisible } =
    useOnboardingPreference(currentSpace?.id);

  // Determine if we should show the setup wizard.
  // Pre-2026-05-29 : "It shows whenever setup is not complete (even if the user
  // already has posts)" — too intrusive once the user has generated content.
  //
  // V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29 — On masque le wizard dès que
  // ≥1 post existe (le user a démarré), SAUF si la préférence explicite est
  // true ET qu'il manque encore quelque chose (charte ou step). L'idée : le
  // wizard est un onboarding, pas un permanent reminder. Reaccessible via
  // SettingsTab → toggle "Afficher le tutoriel d'onboarding".
  //
  // V4-5 fix conservé : si TOUT est complet MAIS la charte graphique est
  // manquante, on garde le CTA "Définir ta charte" tant qu'aucun post n'existe.
  //
  // V4-AGENCY-1 (2026-05-21) — Garde-fou : ne JAMAIS afficher le wizard si
  // `currentSpace` est null.
  //
  // V4-FRONT-WIZARD-POST-GEN-HIDE-VERIFY 2026-05-29 — utilise hasAnyPostProp
  // (raw count avant filtre status) plutôt que filteredPosts.length, sinon
  // le wizard ré-apparaît à tort quand l'user filtre sur un status vide.
  const hasAnyPost = hasAnyPostProp ?? filteredPosts.length > 0;

  // V4-WIZARD-BRAND-FALSE-POSITIVE 2026-06-18 — La charte graphique est DÉCOUPLÉE
  // du gating du wizard : elle ne fait plus partie de `setupIncomplete`. Avant, un
  // faux positif `brandConfigMissing` (charte direction-only considérée manquante)
  // forçait le wizard à rester affiché en permanence même à 4/4. Désormais la charte
  // n'est qu'un CTA bonus rendu dans SetupWizard ; le wizard ne dépend QUE des
  // 4 étapes réelles. Le wizard reste visible tant que (setup incomplet) OU
  // (setup complet mais aucun post encore généré, pour montrer le CTA "Commencer"),
  // et `onboardingVisible` le rend fermable inconditionnellement (cf. handleWizardComplete).
  const setupIncomplete = !isSetupComplete;
  // Un guest (lecture seule) ne configure jamais le space → pas d'onboarding wizard.
  const shouldShowWizard = !isGuest
    && !!currentSpace?.id
    && onboardingVisible
    && (setupIncomplete || !hasAnyPost);

  // V4-WIZARD-02 2026-05-29 — Callback du CTA "Commencer à générer des posts"
  // affiché au pied du wizard quand 4/4 étapes sont complètes.
  // V4-WIZARD-START-CTA-INERT 2026-06-18 — le clic ferme le wizard (now effectif
  // car `onboardingVisible` est inconditionnel dans shouldShowWizard) ET ouvre
  // la modale de génération. Avant, `setOnboardingVisible(false)` seul était sans
  // effet quand l'utilisateur n'avait aucun post (onboardingVisible ignoré), et
  // le bouton n'ouvrait jamais la génération → bouton perçu comme inerte.
  // V4-GEN-CHANNELLESS-DEMO (retiré) — sans canal réellement connecté (compte Zernio
  // connecté, pas juste une ligne channel), on ne peut ni publier ni générer utilement :
  // on redirige vers l'onglet Channels au lieu d'ouvrir la génération (ex-mode démo).
  const hasConnectedChannel = communicationChannels.some(
    (c) => !!c.zernioAccountId && c.zernioStatus === 'connected',
  );
  const handleWizardComplete = () => {
    setOnboardingVisible(false);
    if (hasConnectedChannel) {
      onOpenGenerateModal();
    } else if (onOpenSidebarTab) {
      onOpenSidebarTab(SIDEBAR_ITEMS.CHANNELS);
    }
  };

  // V4-UX-SETUP-EXPRESS 2026-07-02 (Phase 1.1) — L'onboarding par défaut est le
  // « setup express » (depuis l'URL du site). Le SetupWizard 4 étapes classique
  // devient le mode manuel/avancé, accessible via onSwitchToManual.
  const [onboardingMode, setOnboardingMode] = useState<'express' | 'manual'>('express');

  const renderOnboarding = () => {
    if (!onOpenSidebarTab || !currentSpace?.id) return null;
    if (onboardingMode === 'express') {
      return (
        <SetupExpress
          spaceId={currentSpace.id}
          spaceName={currentSpace?.name}
          accountType={user?.accountType}
          steps={setupSteps}
          onRefresh={onRefreshData ?? (async () => {})}
          onSwitchToManual={() => setOnboardingMode('manual')}
          onComplete={handleWizardComplete}
          onOpenSidebarTab={onOpenSidebarTab}
          isMobile={isMobile}
          communicationChannels={communicationChannels}
        />
      );
    }
    return (
      <SetupWizard
        onOpenSidebarTab={onOpenSidebarTab}
        steps={setupSteps}
        onRefresh={onRefreshData}
        brandConfigMissing={brandConfigMissing}
        onComplete={handleWizardComplete}
        accountType={user?.accountType}
        spaceName={currentSpace?.name}
      />
    );
  };

  // Skeleton loading state to prevent wizard flash during initial data fetch
  if (isDataLoading) {
    return (
      <div className={`flex-1 ${isMobile ? 'pt-20 pb-24' : 'px-4 pb-4 -mt-20'} overflow-hidden`}>
        {!isMobile && <div style={{ height: '172px' }} />}
        <div
          className={`grid auto-rows-min ${isMobile ? 'px-4' : 'justify-center'}`}
          style={{
            gridTemplateColumns: isMobile ? 'repeat(1, minmax(0, 100%))' : `repeat(${numColumns}, ${cardWidth}px)`,
            columnGap: `${gridGap}px`,
            rowGap: `${gridGap}px`,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
              <Skeleton className="w-full h-48" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <div className="flex gap-2 pt-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 min-h-0 ${isMobile ? 'pt-20 pb-24' : 'px-4 pb-24 -mt-20'} ${filteredPosts.length > 0 ? 'overflow-auto' : 'overflow-hidden'}`}>
      {!isMobile && <div style={{ height: '172px' }} />}
      
      {filteredPosts.length === 0 ? (
        <div className="flex items-center justify-center" style={{ height: isMobile ? 'calc(100vh - 176px)' : 'calc(100vh - 172px - 80px)' }}>
          {shouldShowWizard && onOpenSidebarTab ? (
            renderOnboarding()
          ) : (
            // UX backlog 11.3 : empty state engageant pour la galerie (post-onboarding).
            <EmptyState
              icon={FileText}
              title={t.posts.noPostsAvailable}
              // En lecture seule (guest/viewer) : description neutre, pas de référence
              // au bouton "Générer" (masqué) — cf V4-FRONT-GUEST-EMPTYSTATE-COPY.
              description={isReadOnly ? t.posts.noPostsDescriptionReadOnly : t.posts.noPostsDescription}
              // Pas de CTA de génération en lecture seule (guest / viewer).
              primaryCta={isReadOnly ? undefined : {
                icon: Wand2,
                label: t.posts.generateNew,
                onClick: onOpenGenerateModal,
              }}
            />
          )}
        </div>
      ) : (
        <>
          {shouldShowWizard && onOpenSidebarTab && (
            <div className={`${isMobile ? 'px-4' : ''}`} style={{ maxWidth: isMobile ? undefined : '672px', margin: '0 auto 24px auto' }}>
              {renderOnboarding()}
            </div>
          )}
          <div
            className={`grid auto-rows-min ${isMobile ? 'px-4' : 'justify-center'}`}
            style={{
              gridTemplateColumns: isMobile ? 'repeat(1, minmax(0, 100%))' : `repeat(${numColumns}, ${cardWidth}px)`,
              columnGap: `${gridGap}px`,
              rowGap: `${gridGap}px`,
            }}
            ref={gridContainerRef}
          >
            {filteredPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onClick={() => setSelectedPost(post)}
                onDelete={() => setDeletingPost(post)}
                communicationChannels={communicationChannels}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
